import { useState, useEffect, useCallback } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import { useMemoArray } from 'hooks/use-memo-array';

import {
  PersonalizedProductsModelNames,
  ProductMenuTypes,
  ProductsProvider,
  useGetPersonalizedProductsV2Query,
} from 'types/graphql';
import { parseProduct, Product } from 'utils/helpers/product';
import filteredProducts from 'shared/graphql/product/filtered-products.gql';

import { GetRecommendationsFallback, Options } from './use-product-recommendations';
import { useVectorRecommendations } from './use-vector-recommendations';
import { useSerializeProductsFromIds } from './use-serialize-products-from-ids';

export type UsePersonalizedItemsV2Return = {
  data?: Product[];
  error?: Error;
  loading: boolean;
};

export function usePersonalizedItemsV2(
  getRecommendationsFallback: GetRecommendationsFallback,
  productIds: string[],
  modelName: PersonalizedProductsModelNames,
  options: Options & { category?: string }
): UsePersonalizedItemsV2Return {
  const { limit = 10, skip = false, excludeProducts = [], category } = options;

  const flags = useFlags();
  const apolloClient = useApolloClient();
  const { dispensary, loading: dispensaryLoading } = useDispensary();
  const { menuType } = useCart();
  const { isKiosk, variant: menuVariant } = useUI();
  const User = useUser();
  const userId = useObserver(() => User.id);

  const productIdsMemo = useMemoArray(productIds);
  const excludeProductsMemo = useMemoArray(excludeProducts);

  const useVectorCartToppersFlag = flags['growth.personalization.vector.cart-toppers.rollout'];
  const useVectorSimilarItemsFlag = flags['growth.personalization.vector.similar-items.rollout'];

  const shouldUseVectorService =
    (modelName === PersonalizedProductsModelNames.cartToppers && useVectorCartToppersFlag) ||
    (modelName === PersonalizedProductsModelNames.relatedItems && useVectorSimilarItemsFlag);

  const [fallbackData, setFallbackData] = useState<Product[] | undefined>(undefined);
  const [fallbackError, setFallbackError] = useState<Error | undefined>(undefined);
  const [fallbackLoading, setFallbackLoading] = useState<boolean>(false);
  const [shouldUseFallback, setShouldUseFallback] = useState<boolean>(false);
  const [fallbackAttempted, setFallbackAttempted] = useState(false);
  const [needsFreshFetch, setNeedsFreshFetch] = useState(false);

  useEffect(() => {
    setNeedsFreshFetch(true);
    setShouldUseFallback(false);
    setFallbackAttempted(false);
  }, [userId]);

  const { productIds: vectorProductIds, loading: vectorLoading, error: vectorError } = useVectorRecommendations({
    productIds,
    limit,
    skip: !shouldUseVectorService,
  });

  const vectorResult = useSerializeProductsFromIds({
    productIds: vectorProductIds,
    excludeProducts: excludeProductsMemo,
    limit,
    skip: !shouldUseVectorService || !!vectorError,
  });

  const getProducts = useCallback(
    async (variables: any, productsProvider = ProductsProvider.dutchie) => {
      const excludeProductsSet = new Set(excludeProductsMemo);
      const filterProduct = ({ id }: Product): boolean => typeof id === 'string' && !excludeProductsSet.has(id);

      const { data } = await apolloClient.query({
        query: filteredProducts,
        variables,
      });

      return data.filteredProducts.products
        .map((product: any) => ({ ...parseProduct(product), provider: productsProvider }))
        .filter(filterProduct);
    },
    [apolloClient, excludeProductsMemo]
  );

  const dispensaryId = dispensary?.id;

  const getVariables = useCallback(
    (productsFilter: any = {}) => {
      if (!dispensaryId) {
        return null;
      }

      return {
        productsFilter: {
          sortBy: 'inputOrder',
          bypassOnlineThresholds: isKiosk,
          dispensaryId,
          isKioskMenu: isKiosk,
          pricingType: menuType,
          Status: 'Active' as const,
          removeProductsBelowOptionThresholds: true,
          userSegmentIds: [],
          ...productsFilter,
        },
        perPage: limit,
      };
    },
    [dispensaryId, isKiosk, limit, menuType]
  );

  const filteredProductIds = productIdsMemo.filter((id): id is string => typeof id === 'string' && id.length > 0);

  const shouldSkipQuery =
    skip ||
    shouldUseVectorService ||
    dispensaryLoading ||
    !dispensaryId ||
    !menuType ||
    !menuVariant ||
    (modelName !== PersonalizedProductsModelNames.recommendedForYou && filteredProductIds.length === 0);

  const fetchPolicy = needsFreshFetch ? 'network-only' : 'cache-first';

  const { data: queryData, loading: queryLoading, error: queryError } = useGetPersonalizedProductsV2Query({
    variables: {
      input: {
        dispensaryId: dispensaryId || '',
        limit,
        filters: {
          menuType: menuType || ProductMenuTypes.rec,
          menuVariant: menuVariant || 'default',
          ...(category && { category }),
        },
        modelName,
        productIds: filteredProductIds,
      },
    },
    skip: shouldSkipQuery,
    errorPolicy: 'all',
    fetchPolicy,
  });

  useEffect(() => {
    if (needsFreshFetch && !queryLoading && queryData) {
      setNeedsFreshFetch(false);
    }
  }, [needsFreshFetch, queryLoading, queryData]);

  useEffect(() => {
    if (queryData?.getPersonalizedProductsV2?.products.length) {
      setFallbackAttempted(false);
      setShouldUseFallback(false);
    }
  }, [queryData]);

  useEffect(() => {
    if (shouldSkipQuery || fallbackAttempted) {
      return;
    }

    const hasError = queryError;
    const hasNoData = queryData?.getPersonalizedProductsV2 && !queryData.getPersonalizedProductsV2.products.length;

    if ((hasError || hasNoData) && !shouldUseFallback && !fallbackLoading) {
      setShouldUseFallback(true);
      setFallbackLoading(true);
      setFallbackAttempted(true);

      try {
        getRecommendationsFallback((productFilters) => {
          const variables = getVariables(productFilters);
          if (!variables) {
            throw new Error('Missing dispensary ID');
          }
          return getProducts(variables);
        })
          .then(setFallbackData)
          .catch(setFallbackError)
          .finally(() => setFallbackLoading(false));
      } catch (syncError) {
        setFallbackError(syncError as Error);
        setFallbackLoading(false);
      }
    }
    // Intentionally excludes callback dependencies (getRecommendationsFallback, getProducts, getVariables)
    // because these may recreate on state changes but should not trigger fallback re-execution.
    // The effect should only respond to query state changes (queryError, queryData).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryError, queryData, shouldUseFallback, fallbackLoading, shouldSkipQuery, fallbackAttempted]);

  useEffect(() => {
    if (!shouldUseVectorService || !vectorError || shouldUseFallback || fallbackLoading) {
      return;
    }

    setShouldUseFallback(true);
    setFallbackLoading(true);

    getRecommendationsFallback((productFilters) => {
      const variables = getVariables(productFilters);
      if (!variables) {
        throw new Error('Missing dispensary ID');
      }
      return getProducts(variables);
    })
      .then(setFallbackData)
      .catch(setFallbackError)
      .finally(() => setFallbackLoading(false));
  }, [
    shouldUseVectorService,
    vectorError,
    shouldUseFallback,
    fallbackLoading,
    getRecommendationsFallback,
    getProducts,
    getVariables,
  ]);

  // Reset fallback state when productIds change or when vector service succeeds
  useEffect(() => {
    if (!shouldUseVectorService) {
      return;
    }

    // Reset fallback if we have successful vector data or if productIds changed (indicating navigation)
    if (shouldUseFallback && (vectorResult.data || (!vectorError && !vectorLoading))) {
      setShouldUseFallback(false);
      setFallbackData(undefined);
      setFallbackError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseVectorService, vectorResult.data, vectorError, vectorLoading, shouldUseFallback, productIdsMemo]);

  const processedData = queryData?.getPersonalizedProductsV2?.products
    ? queryData.getPersonalizedProductsV2.products
        .map((product: any) => parseProduct(product))
        .filter((product: Product) => {
          const excludeProductsSet = new Set(excludeProductsMemo);
          return typeof product.id === 'string' && !excludeProductsSet.has(product.id);
        })
    : undefined;

  if (shouldUseVectorService) {
    if (shouldUseFallback) {
      return {
        data: fallbackData,
        loading: fallbackLoading,
        error: fallbackError,
      };
    }

    return {
      ...vectorResult,
      loading: vectorLoading || vectorResult.loading,
      error: vectorError ?? vectorResult.error,
    };
  }

  if (shouldUseFallback) {
    return {
      data: fallbackData,
      loading: fallbackLoading,
      error: fallbackError,
    };
  }

  return {
    data: processedData,
    loading: queryLoading || dispensaryLoading,
    error: queryError ? new Error(queryError.message) : undefined,
  };
}
